/**
 * AppLogsPanel - UI para visualização e exportação de logs persistentes
 * Ativado por 5 toques no avatar do usuário
 */

import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { persistentLogger, LogCategory, LogLevel, PersistentLog } from '@/lib/persistent-logger';
import { Copy, Share2, Trash2, Search, Filter, RefreshCw, AlertCircle, Cloud, CloudOff } from 'lucide-react';
import { toast } from 'sonner';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { motion, AnimatePresence } from 'framer-motion';

interface AppLogsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AppLogsPanel = ({ open, onOpenChange }: AppLogsPanelProps) => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<LogCategory | 'all'>('all');
  const [levelFilter, setLevelFilter] = useState<LogLevel | 'all'>('all');
  const [timeRange, setTimeRange] = useState<'1h' | '6h' | '24h' | 'all'>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [logs, setLogs] = useState(persistentLogger.getLogs());
  const [syncStatus, setSyncStatus] = useState(persistentLogger.getSyncStatus());

  // Auto-refresh every 2 seconds
  useEffect(() => {
    if (!autoRefresh || !open) return;
    
    const interval = setInterval(() => {
      setLogs(persistentLogger.getLogs());
      setSyncStatus(persistentLogger.getSyncStatus());
    }, 2000);

    return () => clearInterval(interval);
  }, [autoRefresh, open]);

  // Refresh logs when dialog opens
  useEffect(() => {
    if (open) {
      setLogs(persistentLogger.getLogs());
    }
  }, [open]);

  // Filtrar logs
  const filteredLogs = useMemo(() => {
    const now = Date.now();
    const timeRanges = {
      '1h': now - 60 * 60 * 1000,
      '6h': now - 6 * 60 * 60 * 1000,
      '24h': now - 24 * 60 * 60 * 1000,
      'all': 0
    };

    let filtered = logs.filter(log => {
      const matchesCategory = categoryFilter === 'all' || log.category === categoryFilter;
      const matchesLevel = levelFilter === 'all' || log.level === levelFilter;
      const matchesTime = timeRange === 'all' || log.timestamp >= timeRanges[timeRange];
      return matchesCategory && matchesLevel && matchesTime;
    });

    if (search) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(search.toLowerCase()) ||
        JSON.stringify(log.data).toLowerCase().includes(search.toLowerCase())
      );
    }

    return filtered;
  }, [logs, search, categoryFilter, levelFilter, timeRange]);

  const stats = persistentLogger.getStats();
  
  // Count logs by category
  const logCounts = logs.reduce((acc, log) => {
    acc[log.category] = (acc[log.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const errorCount = logs.filter(l => l.level === 'error' || l.level === 'fatal').length;

  const handleCopyAll = () => {
    const exported = persistentLogger.exportLogs({
      category: categoryFilter !== 'all' ? categoryFilter : undefined,
      level: levelFilter !== 'all' ? levelFilter : undefined
    });

    navigator.clipboard.writeText(exported).then(() => {
      toast.success('Logs copiados!', {
        description: `${filteredLogs.length} logs copiados para a área de transferência`
      });
    }).catch(() => {
      toast.error('Erro ao copiar logs');
    });
  };

  const handleCopyRecent = () => {
    const recentLogs = filteredLogs.slice(0, 50);
    navigator.clipboard.writeText(JSON.stringify(recentLogs, null, 2)).then(() => {
      toast.success('Últimos 50 logs copiados!');
    }).catch(() => {
      toast.error('Erro ao copiar logs');
    });
  };

  const handleShare = async () => {
    const exported = persistentLogger.exportLogs({
      category: categoryFilter !== 'all' ? categoryFilter : undefined,
      level: levelFilter !== 'all' ? levelFilter : undefined
    });

    if (Capacitor.isNativePlatform()) {
      try {
        await Share.share({
          title: 'YBR - Logs do App',
          text: exported,
          dialogTitle: 'Compartilhar Logs'
        });
      } catch (error) {
        toast.error('Erro ao compartilhar logs');
      }
    } else {
      // Web: usar clipboard
      await handleCopyAll();
    }
  };

  const handleClearLogs = async () => {
    if (confirm('Tem certeza que deseja limpar todos os logs?')) {
      await persistentLogger.clearLogs();
      setLogs([]);
      toast.success('Logs limpos com sucesso');
    }
  };

  const handleRefresh = () => {
    setLogs(persistentLogger.getLogs());
    setSyncStatus(persistentLogger.getSyncStatus());
    toast.success('Logs atualizados');
  };

  const handleSyncNow = async () => {
    toast.loading('Sincronizando logs...', { id: 'sync' });
    
    const result = await persistentLogger.syncToBackend();
    
    if (result.success) {
      if (result.syncedCount > 0) {
        toast.success(`${result.syncedCount} logs sincronizados!`, { id: 'sync' });
      } else {
        toast.success('Nenhum log pendente para sincronizar', { id: 'sync' });
      }
      setLogs(persistentLogger.getLogs());
      setSyncStatus(persistentLogger.getSyncStatus());
    } else {
      toast.error(`Erro ao sincronizar: ${result.error}`, { id: 'sync' });
    }
  };

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case 'fatal':
      case 'error':
        return 'text-red-500 bg-red-500/10';
      case 'warn':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'info':
        return 'text-blue-500 bg-blue-500/10';
      case 'debug':
        return 'text-gray-500 bg-gray-500/10';
      default:
        return 'text-foreground bg-muted';
    }
  };

  const getCategoryEmoji = (category: LogCategory) => {
    switch (category) {
      case 'BLE': return '📡';
      case 'SPOTIFY': return '🎵';
      case 'GPS': return '📍';
      case 'AUTH': return '🔐';
      case 'NETWORK': return '🌐';
      case 'APP': return '📱';
      case 'OAUTH': return '🔑';
      case 'HEALTHKIT': return '❤️';
      case 'GOOGLEFIT': return '🏃';
      case 'PUSH': return '🔔';
      case 'WEARABLE': return '⌚';
      case 'MUSIC': return '🎶';
      case 'STORAGE': return '💾';
      default: return '📝';
    }
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span>📊 Logs do Aplicativo</span>
              {errorCount > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex items-center gap-1 px-2 py-1 bg-destructive/20 rounded-md"
                >
                  <AlertCircle className="w-4 h-4 text-destructive" />
                  <span className="text-xs font-medium text-destructive">{errorCount} erros</span>
                </motion.div>
              )}
              {syncStatus.unsyncedCount > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex items-center gap-1 px-2 py-1 bg-amber-500/20 rounded-md"
                >
                  <CloudOff className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-amber-500">
                    {syncStatus.unsyncedCount} pendentes
                  </span>
                </motion.div>
              )}
            </div>
            <Badge variant="outline">{stats.total} logs</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Filtros */}
        <div className="px-6 py-4 space-y-4 border-b bg-muted/20">
          {/* Barra de busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar nos logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filtros - Categories com contadores */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={categoryFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter('all')}
            >
              Todos ({logs.length})
            </Button>
            {(['BLE', 'SPOTIFY', 'GPS', 'AUTH', 'NETWORK', 'APP', 'OAUTH', 'HEALTHKIT', 'GOOGLEFIT', 'PUSH', 'WEARABLE'] as LogCategory[]).map(cat => (
              logCounts[cat] > 0 && (
                <Button
                  key={cat}
                  variant={categoryFilter === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategoryFilter(cat)}
                  className="relative"
                >
                  {getCategoryEmoji(cat)} {cat}
                  <Badge variant="secondary" className="ml-2 px-1 py-0 h-4 text-[10px]">
                    {logCounts[cat]}
                  </Badge>
                </Button>
              )
            ))}
          </div>

          {/* Level and Time filters */}
          <div className="flex gap-2">
            <Select value={levelFilter} onValueChange={(v) => setLevelFilter(v as any)}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos níveis</SelectItem>
                <SelectItem value="debug">Debug</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warn">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="fatal">Fatal</SelectItem>
              </SelectContent>
            </Select>

            <Select value={timeRange} onValueChange={(v) => setTimeRange(v as any)}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">Última 1h</SelectItem>
                <SelectItem value="6h">Últimas 6h</SelectItem>
                <SelectItem value="24h">Últimas 24h</SelectItem>
                <SelectItem value="all">Todo período</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Estatísticas rápidas */}
          <div className="flex gap-2 flex-wrap">
            {stats.byLevel.error > 0 && (
              <Badge variant="destructive">
                ❌ {stats.byLevel.error} erros
              </Badge>
            )}
            {stats.byLevel.warn > 0 && (
              <Badge className="bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20">
                ⚠️ {stats.byLevel.warn} warnings
              </Badge>
            )}
            {stats.byLevel.info > 0 && (
              <Badge variant="secondary">
                ℹ️ {stats.byLevel.info} info
              </Badge>
            )}
          </div>
        </div>

        {/* Lista de logs */}
        <ScrollArea className="flex-1 px-6">
          <div className="space-y-2 py-4">
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>Nenhum log encontrado</p>
                <p className="text-sm mt-2">
                  {search || categoryFilter !== 'all' || levelFilter !== 'all'
                    ? 'Tente ajustar os filtros'
                    : 'Os logs aparecerão aqui conforme você usa o app'}
                </p>
              </div>
            ) : (
              <AnimatePresence>
                {filteredLogs.map((log) => (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className={`p-3 rounded-lg border ${
                      log.level === 'error' || log.level === 'fatal'
                        ? 'bg-destructive/10 border-destructive animate-pulse'
                        : log.level === 'warn'
                        ? 'bg-yellow-500/10 border-yellow-500/50'
                        : 'bg-card hover:bg-accent/50'
                    } transition-colors`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getCategoryEmoji(log.category)}</span>
                        <Badge className={getLevelColor(log.level)} variant="outline">
                          {log.level}
                        </Badge>
                        <Badge variant="outline">{log.category}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatTime(log.timestamp)}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-sm font-medium mb-1">{log.message}</p>
                    
                    {log.data && (
                      <details className="text-xs text-muted-foreground mt-2">
                        <summary className="cursor-pointer hover:text-foreground">
                          Ver dados
                        </summary>
                        <pre className="mt-2 p-2 bg-muted rounded overflow-x-auto">
                          {JSON.stringify(log.data, null, 2)}
                        </pre>
                      </details>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </ScrollArea>

        {/* Ações */}
        <div className="px-6 py-4 border-t flex gap-2">
          <Button
            variant={syncStatus.unsyncedCount > 0 ? "default" : "outline"}
            size="sm"
            onClick={handleSyncNow}
            disabled={syncStatus.isSyncing}
          >
            {syncStatus.isSyncing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <Cloud className="w-4 h-4 mr-2" />
                Sincronizar
                {syncStatus.unsyncedCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {syncStatus.unsyncedCount}
                  </Badge>
                )}
              </>
            )}
          </Button>
          <Button 
            variant={autoRefresh ? "default" : "outline"} 
            size="sm" 
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            Auto
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopyRecent}>
            <Copy className="w-4 h-4 mr-2" />
            50
          </Button>
          <Button
            onClick={handleCopyAll}
            variant="outline"
            size="sm"
            disabled={filteredLogs.length === 0}
          >
            <Copy className="w-4 h-4 mr-2" />
            Todos
          </Button>
          <Button
            onClick={handleShare}
            variant="outline"
            size="sm"
            disabled={filteredLogs.length === 0}
          >
            <Share2 className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleClearLogs}
            variant="destructive"
            size="sm"
            disabled={stats.total === 0}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Limpar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};